/**
 * WEB-903 (option B): DatoCMS editors sometimes author an internal link as a
 * full absolute URL (e.g. `https://zoe.com/daily30`). Our CTA blocks treat any
 * `http(s)`/`//` href as external and do a `window.location.href` HARD reload —
 * which wipes the in-memory `lead_id` even though it's really an internal hop.
 *
 * This normalises a first-party absolute URL back to a relative path so the
 * navigation stays a client-side (SPA) transition and the in-memory id survives.
 * Third-party URLs are returned untouched (we must never relativise — or carry
 * the id onto — a different site).
 *
 * First-party hosts are derived from env (inlined into the client bundle), so
 * the result is identical on server and client — no hydration mismatch.
 */

let firstPartyHosts: Set<string> | null = null

export function getFirstPartyHosts(): Set<string> {
  if (firstPartyHosts) return firstPartyHosts

  // NEXT_PUBLIC_SITE_URL is inlined on both server and client, so the resolved
  // set is identical in each (no hydration mismatch).
  const candidates = [
    process.env.NEXT_PUBLIC_SITE_URL,
    'https://zoe.com',
    'https://www.zoe.com',
    'https://joinzoe.com',
    'https://www.joinzoe.com',
  ]
  const hosts = new Set<string>()
  for (const candidate of candidates) {
    if (!candidate) continue
    try {
      hosts.add(new URL(candidate).host)
    } catch {
      // ignore malformed values
    }
  }
  firstPartyHosts = hosts
  return hosts
}

/**
 * Returns a relative `path?query#hash` when `url` is an absolute first-party
 * URL; otherwise returns `url` unchanged (relative paths, hashes and
 * third-party URLs all pass through).
 */
export function toFirstPartyRelativeUrl(url: string): string {
  if (!url) return url
  const isProtocolRelative = url.startsWith('//')
  if (!isProtocolRelative && !/^https?:\/\//i.test(url)) return url
  try {
    const parsed = new URL(isProtocolRelative ? `https:${url}` : url)
    if (getFirstPartyHosts().has(parsed.host)) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    }
  } catch {
    // malformed URL — leave it as-is
  }
  return url
}

export type CmsLinkTarget = { kind: 'internal'; href: string } | { kind: 'external'; href: string }

// `zoe.com/en-us/daily30`, `www.zoe.com`: a dotted host followed by a path, query, hash or nothing.
const BARE_HOST_PATTERN = /^[a-z0-9-]+(\.[a-z0-9-]+)+(?=[/?#]|$)/i

/**
 * Resolves a free-text CMS link field into somewhere we can navigate, or null.
 *
 * Next's router treats anything without a leading `/` or a scheme as relative to the current
 * page, so `zoe.com/en-us/daily30` lands on `/en-gb/zoe.com/en-us/daily30` and 404s (Cyber5 2025
 * banner). Accepted forms:
 * - root-relative paths (`/en-us/daily30`) → internal
 * - `http(s)://` and `//` URLs → internal when the host is ours, external otherwise
 * - bare hosts (`zoe.com/...`, `www.zoe.com/...`) → read as `https://`, then as above
 *
 * Anything else (page-relative paths, `mailto:`, `javascript:`) returns null.
 *
 * Only call this from event handlers: it counts the serving origin as ours, which differs between
 * server and client.
 */
export function resolveCmsLinkTarget(url: string | null | undefined): CmsLinkTarget | null {
  const trimmed = url?.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return { kind: 'internal', href: trimmed }

  let absolute: string
  if (/^https?:\/\//i.test(trimmed)) absolute = trimmed
  else if (trimmed.startsWith('//')) absolute = `https:${trimmed}`
  else if (BARE_HOST_PATTERN.test(trimmed)) absolute = `https://${trimmed}`
  else return null

  try {
    const parsed = new URL(absolute)
    if (isFirstPartyHostForLeadId(parsed.host)) {
      return { kind: 'internal', href: `${parsed.pathname}${parsed.search}${parsed.hash}` }
    }
    return { kind: 'external', href: parsed.toString() }
  } catch {
    return null
  }
}

/**
 * True when `host` is one of ours for the purpose of CARRYING the lead_id.
 *
 * Deliberately wider than `getFirstPartyHosts`: it also accepts the origin the page is served from,
 * which is first-party by definition. Without it, a deployment on any other domain — stage, a Vercel
 * preview, a branch deploy — drops the id from every absolute self-referencing URL it builds, the
 * Stripe return_url included, and attribution dies at the redirect back (WEB-1262).
 *
 * Kept out of `getFirstPartyHosts` because that set also drives `toFirstPartyRelativeUrl`, which
 * runs during render on both server and client and must resolve identically in each. This is only
 * reached from `appendLeadId`, which no-ops until the provider has captured an id in an effect, so
 * it never runs during the first render and cannot desync it.
 */
export function isFirstPartyHostForLeadId(host: string): boolean {
  if (getFirstPartyHosts().has(host)) return true
  return typeof window !== 'undefined' && host === window.location.host
}
